import {Chip} from '@dropbox/dig-components/chip';
import {Spinner} from '@dropbox/dig-components/progress_indicators';
import {atoms, Box} from '@dropbox/dig-foundations';
import {UIIcon} from '@dropbox/dig-icons';
import {
  CodeLine,
  CollectionLine,
  FollowLine,
  NotificationLine,
  SlackExternalLogo,
  StacksLine,
} from '@dropbox/dig-icons/assets';
import type {DcdOwnershipPageType} from 'analytics/dcdOwnershipAnalytics';
import {logDcdOwnershipDetailInteraction} from 'analytics/dcdOwnershipAnalytics';
import {
  DcdOwnershipComponent,
  DcdOwnershipService,
  DcdOwnershipServiceReferenceDetail,
} from 'client';
import {Link} from 'components/DSYS/Link';
import {useEmployee} from 'hooks/useEmployee';
import {t} from 'i18next';
import type {ComponentType, SVGProps} from 'react';

import {OwnershipDetailLayout} from './OwnershipDetailLayout';
import {
  CODE_PATH_REFERENCE_TYPE,
  getGrouperGroupUrl,
  getPagerDutyServiceUrl,
  getServicesPpServiceUrl,
  getSlackChannelUrl,
  getSourcegraphCodePathUrl,
  SERVICE_ROUTE_REFERENCE_TYPE,
  useDcdOwnershipPageType,
} from './ownershipHelpers';
import {OwnershipServiceGraph} from './OwnershipServiceGraph';
import {OwnershipServicesCard} from './OwnershipServicesCard';
import {TeamMembersCard} from './TeamMembersCard';

const PAGE_SOURCE = 'dcd_ownership_detail';

const OwnerMetadataChip = ({
  label,
  to,
  icon,
  chipType,
  pageType,
}: {
  label: string;
  to: string;
  icon: ComponentType<SVGProps<SVGSVGElement>>;
  chipType: string;
  pageType?: DcdOwnershipPageType;
}) => {
  return (
    <Link
      to={to}
      hasNoUnderline
      monochromatic
      openInNewTab
      rel="noopener noreferrer"
      aria-label={label}
      onClick={
        pageType
          ? () => logDcdOwnershipDetailInteraction('metadata_chip_clicked', {chipType, pageType})
          : undefined
      }
    >
      <Chip
        type="button"
        tabIndex={-1}
        variant="attention"
        size="small"
        style={{pointerEvents: 'none'}}
      >
        <Chip.IconAccessory>
          <UIIcon src={icon} />
        </Chip.IconAccessory>
        <Chip.Content>{label}</Chip.Content>
      </Chip>
    </Link>
  );
};

const OwnershipMetadataChips = ({
  component,
  selectedService,
  selectedReference,
  pageType,
}: {
  component: DcdOwnershipComponent;
  selectedService?: DcdOwnershipService;
  selectedReference?: DcdOwnershipServiceReferenceDetail;
  pageType?: DcdOwnershipPageType;
}) => {
  const ownerGroupName = component.owner_group_name;
  const pagerdutyAlertingServiceId = component.owner_group_pagerduty_alerting_service_id;
  const detailService = selectedReference?.service ?? selectedService;
  const showComponentOwnerChips =
    !detailService || detailService.owner_name === component.owner_group_name;
  const slackChannelPublic = component.owner_group_slack_channel_public;
  const slackChannelName = slackChannelPublic?.replace(/^#/, '');
  const slackChannelLabel = slackChannelName ? `#${slackChannelName}` : null;
  const sourcegraphCodePath =
    selectedReference?.reference_type === CODE_PATH_REFERENCE_TYPE ? selectedReference.name : null;
  if (
    !ownerGroupName &&
    !pagerdutyAlertingServiceId &&
    !slackChannelPublic &&
    !selectedService &&
    !sourcegraphCodePath
  ) {
    return null;
  }

  return (
    <Box paddingTop="4" display="flex" flexWrap="wrap" style={{gap: 8}}>
      {ownerGroupName && showComponentOwnerChips && (
        <OwnerMetadataChip
          label={t('dcd_ownership_grouper_group_name', {group: ownerGroupName})}
          to={getGrouperGroupUrl(ownerGroupName)}
          icon={CollectionLine}
          chipType="grouper"
          pageType={pageType}
        />
      )}
      {pagerdutyAlertingServiceId && showComponentOwnerChips && (
        <OwnerMetadataChip
          label={t('dcd_ownership_pagerduty_alerting_service_id', {
            serviceId: pagerdutyAlertingServiceId,
          })}
          to={getPagerDutyServiceUrl(pagerdutyAlertingServiceId)}
          icon={NotificationLine}
          chipType="pagerduty"
          pageType={pageType}
        />
      )}
      {slackChannelName && slackChannelLabel && showComponentOwnerChips && (
        <OwnerMetadataChip
          label={t('dcd_ownership_slack_channel_public', {
            channel: slackChannelLabel,
          })}
          to={getSlackChannelUrl(slackChannelName)}
          icon={SlackExternalLogo}
          chipType="slack"
          pageType={pageType}
        />
      )}
      {sourcegraphCodePath && (
        <OwnerMetadataChip
          label={t('dcd_ownership_sourcegraph_label')}
          to={getSourcegraphCodePathUrl(sourcegraphCodePath)}
          icon={CodeLine}
          chipType="sourcegraph"
          pageType={pageType}
        />
      )}
      {selectedService && (
        <OwnerMetadataChip
          label={t('dcd_ownership_services_pp_label')}
          to={getServicesPpServiceUrl(selectedService.name)}
          icon={StacksLine}
          chipType="services_pp"
          pageType={pageType}
        />
      )}
    </Box>
  );
};

export const OwnershipDetail = ({
  component,
  title = component.name,
  description = component.description,
  selectedService,
  selectedReference,
  showServices = true,
  showServiceGraph = true,
}: {
  component: DcdOwnershipComponent;
  title?: string;
  description?: string | null;
  selectedService?: DcdOwnershipService;
  selectedReference?: DcdOwnershipServiceReferenceDetail;
  showServices?: boolean;
  showServiceGraph?: boolean;
}) => {
  const ownerGroupName = component.owner_group_name;
  const owners = component.owners ?? [];
  const services = component.services ?? [];
  const detailService = selectedReference?.service ?? selectedService;
  const oncallEmail = detailService?.pagerduty_current_oncall_email;
  const oncallLdap = oncallEmail?.split('@')[0];
  const {employee: oncallEmployee, isLoading: isOncallLoading} = useEmployee({
    ldap: oncallLdap,
    throwOnError: false,
  });
  const oncallMembers = oncallEmployee
    ? [
        {
          user_id: oncallEmployee.user_id,
          ldap: oncallEmployee.ldap,
          name: oncallEmployee.name,
          role: oncallEmployee.role,
        },
      ]
    : oncallEmail && oncallLdap && !isOncallLoading
      ? [{user_id: oncallEmail, ldap: oncallLdap, name: oncallEmail}]
      : [];
  const graphServices = selectedReference
    ? [selectedReference.service]
    : selectedService
      ? [selectedService]
      : services;
  const pageType = useDcdOwnershipPageType(selectedReference?.reference_type);
  const titleIcon = selectedService
    ? {
        className: atoms({color: 'Text Base'}),
        label: 'Service',
        src: StacksLine,
      }
    : selectedReference?.reference_type === SERVICE_ROUTE_REFERENCE_TYPE
      ? {
          className: atoms({color: 'Primary Base'}),
          label: 'Service route',
          src: FollowLine,
        }
      : selectedReference?.reference_type === CODE_PATH_REFERENCE_TYPE
        ? {
            className: atoms({color: 'Text Subtle'}),
            label: 'Service path',
            src: CodeLine,
          }
        : undefined;
  return (
    <OwnershipDetailLayout
      title={title}
      description={description}
      titleAccessory={
        titleIcon ? (
          <UIIcon
            src={titleIcon.src}
            aria-label={titleIcon.label}
            className={titleIcon.className}
          />
        ) : undefined
      }
      headerContent={
        <OwnershipMetadataChips
          component={component}
          selectedService={selectedService}
          selectedReference={selectedReference}
          pageType={pageType}
        />
      }
    >
      {detailService && oncallEmail ? (
        <TeamMembersCard
          members={oncallMembers}
          title={t('oncall')}
          icon={NotificationLine}
          beforeMembers={isOncallLoading ? <Spinner size="small" /> : undefined}
          source={PAGE_SOURCE}
          onClick={
            pageType
              ? () => logDcdOwnershipDetailInteraction('oncall_clicked', {pageType})
              : undefined
          }
        />
      ) : !detailService && owners.length > 0 ? (
        <TeamMembersCard
          members={owners}
          title={t('owners')}
          source={PAGE_SOURCE}
          onClick={
            pageType
              ? () => logDcdOwnershipDetailInteraction('owner_clicked', {pageType})
              : undefined
          }
        />
      ) : null}
      {showServices && services.length > 0 && (
        <OwnershipServicesCard
          componentId={component.id}
          services={services}
          onClick={
            pageType
              ? () => logDcdOwnershipDetailInteraction('service_clicked', {pageType})
              : undefined
          }
        />
      )}
      {showServiceGraph && ownerGroupName && graphServices.length > 0 && (
        <OwnershipServiceGraph
          componentId={component.id}
          componentName={component.name}
          componentDescription={component.description}
          ownerGroupName={ownerGroupName}
          services={graphServices}
          initiallyExpandedServiceId={selectedService?.id}
          isServiceDetail={selectedService !== undefined}
          selectedReference={selectedReference}
        />
      )}
    </OwnershipDetailLayout>
  );
};
